/**
 * v2 API contract of «Поступления» (/receipts): the invoices issued for a month, grouped by payer
 * and document, with what was received (docs/research/design2-month-ops.md §4, §6.4;
 * decisions-log R2, V27).
 */
import { z } from 'zod';
import { isIsoDate, isPeriod } from '../dates';
import type { FxEstimate } from './fxEstimate';

export const receiptsQuerySchema = z.object({
  month: z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ'),
  issuer: z.string().min(1).default('all'),
});
export type ReceiptsQuery = z.infer<typeof receiptsQuerySchema>;

/**
 * What a row stands for (V63): our or the client's invoice, a payroll statement, or the money of a
 * project that needs no invoice — its record is written with the first payment.
 */
export type ReceiptDocKind = 'invoice' | 'statement' | 'none';

/** One document of the month, or a month without one of a project that needs no invoice. */
export interface ReceiptDoc {
  /** null while a project without invoices has no record for the month yet. */
  readonly invoiceId: string | null;
  /** The project of a month without a record (what its first payment is recorded for). */
  readonly projectId: string | null;
  readonly kind: ReceiptDocKind;
  /** Our or the client's number; null for a statement or a project without invoices. */
  readonly number: string | null;
  /** The document's date; null without a record. */
  readonly date: string | null;
  readonly currency: string;
  /** null when the month's amount of a project without invoices is not known. */
  readonly amountMinor: number | null;
  /** Received net of bank fees. */
  readonly receivedMinor: number;
  readonly feeMinor: number;
  /** What is still expected; null when the amount is not known. */
  readonly openMinor: number | null;
  /** Days past due while open; 0 otherwise. */
  readonly overdueDays: number;
  readonly latestPaidOn: string | null;
  /** The newest payment allocated to the document («Отменить»); null without one. */
  readonly lastPaymentId: string | null;
  /**
   * Due at the rate of the payment day (V185): until paid, the amount is today's rate's and a
   * payment above it is not refused here — the server fixes it at the payment's rate.
   */
  readonly isPaidAtPaymentRate: boolean;
  /** Until then `amountMinor` is at the latest known rate, not the final one (V185, V197). */
  readonly amountEstimate: FxEstimate | null;
  /** «Цена 3 040,00 USD — к оплате по курсу на дату оплаты»; null for an invoice in its price currency. */
  readonly fxTitle: string | null;
}

export interface ReceiptClient {
  readonly clientId: string;
  readonly clientName: string;
  /** Open amount per currency, first-seen order; only currencies with something open. */
  readonly open: ReadonlyArray<{ readonly currency: string; readonly amountMinor: number }>;
  readonly docs: readonly ReceiptDoc[];
}

export interface ReceiptsResponse {
  readonly period: string;
  readonly clients: readonly ReceiptClient[];
}

/**
 * What happens to a part not received or not paid (V64): `carry` keeps it open (a payout's
 * difference moves to the next month), `settle` closes the document or payout — the shortfall of an
 * invoice is booked as the bank fee, a month without an invoice takes the received sum as its
 * amount, a payout carries nothing.
 */
export const REMAINDERS = ['carry', 'settle'] as const;
export type Remainder = (typeof REMAINDERS)[number];

/**
 * POST /receipts — money received for one document (R2: the allocation is amount + fee), or for a
 * month of a project that needs no invoice (`projectId` + `period`, V63). Nothing received is
 * refused: there is no «Списать долг» (V209).
 */
export const recordReceiptSchema = z
  .object({
    invoiceId: z.string().min(1).optional(),
    projectId: z.string().min(1).optional(),
    period: z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ').optional(),
    amountMinor: z.number().int().min(0, 'Введите полученную сумму'),
    feeMinor: z.number().int().min(0, 'Комиссия — число от 0').default(0),
    paidOn: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД').optional(),
    remainder: z.enum(REMAINDERS).default('carry'),
  })
  .refine((body) => !!body.invoiceId || (!!body.projectId && !!body.period), {
    message: 'Укажите счёт или проект и месяц',
  })
  .refine((body) => body.amountMinor > 0, {
    message: 'Введите полученную сумму',
    path: ['amountMinor'],
  });
export type RecordReceipt = z.input<typeof recordReceiptSchema>;

/**
 * PUT /receipts/:invoiceId/received — what was received for a document, as typed in «Получено» and
 * «Комиссия» (V129): it replaces the document's payments with one; `null` clears them.
 */
export const setReceivedSchema = z.object({
  amountMinor: z.number().int().min(0, 'Введите полученную сумму').nullable(),
  feeMinor: z.number().int().min(0, 'Комиссия — число от 0').default(0),
});
export type SetReceived = z.input<typeof setReceivedSchema>;
export type SetReceivedBody = z.output<typeof setReceivedSchema>;
export type RecordReceiptBody = z.output<typeof recordReceiptSchema>;

export interface ReceiptWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
}
